import { useState, useEffect, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, MessageCircle } from 'lucide-react';
import { apiFetch, ApiClientError, clearTokens } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import misesparkSymbol from '@/assets/logo/misespark-symbol-full-light.svg';

/**
 * v0.4 追加要件⑲: パスワードのセルフリセット申請。
 * 設計 (正典): docs/design/v04-self-service-password-reset-detail.md §5
 *
 * ⚠️ D-422 (ユーザー列挙対策): サーバーはアカウントの有無・メール登録の有無・レート制限の有無に
 * 関わらず常に同一の成功応答を返す。画面も同じで、「送信しました」ではなく
 * 「該当するアカウントがあれば送信しました」という条件付きの文言にすること。
 *
 * ⚠️ D-418: メール経路は送信元ドメイン未取得のため全テナントでフラグ OFF。つまり現時点で
 * 実際に届くのは LINE 経路だけであり、この画面は「LINE / 管理者依頼への案内」を必ず併記する。
 * 案内が無いと、利用者は送信を待ち続けて復旧できない。
 */
export function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const setUser = useAuthStore((s) => s.setUser);

  // §8-73 と同型の予防: 既存セッションが残っているブラウザで復旧フローを開くケースがあるため、
  // ここでも明示的にセッションを破棄する (SetupPasswordPage と揃える)。
  useEffect(() => {
    clearTokens();
    setUser(null);
  }, [setUser]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await apiFetch<{ accepted: boolean }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      setSubmitted(true);
    } catch (err) {
      // 429 (IP 単位のレート制限) だけは利用者に伝える必要がある。
      // ⚠️ アカウント単位の制限はサーバー側で沈黙スキップされるため、ここには来ない (D-497)。
      if (err instanceof ApiClientError) {
        setError(err.code === 'RATE_LIMITED'
          ? '短時間に何度も試行されています。しばらく待ってからもう一度お試しください'
          : err.message);
      } else {
        setError('通信エラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-canvas px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-surface-card rounded-xl shadow-overlay p-8">
          {/* ⚠️ 見出しの構造は LoginPage / SetupPasswordPage と揃える (h1 = 製品名のワードマーク、
              用途は補助テキスト)。ここだけ h1 を用途にすると、未ログインの3画面で階層が食い違い、
              スクリーンリーダー利用者にはサービス名が一切露出しない (フィッシング耐性の観点)。 */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 justify-center mb-2">
              <img src={misesparkSymbol} alt="" className="w-12 h-12" />
            </div>
            <h1 className="text-[26px] font-extrabold tracking-tight">
              <span className="text-misespark-navy">Mise</span>
              <span className="bg-gradient-to-r from-misespark-orange to-misespark-yellow bg-clip-text text-transparent">Spark</span>
            </h1>
            <p className="text-sm text-text-sub mt-1">パスワードの再設定</p>
          </div>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-md bg-danger-bg text-danger-text text-[13px] border border-danger-border flex items-start gap-2"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>{error}</span>
            </div>
          )}

          {submitted ? (
            <div className="space-y-4">
              <div
                role="status"
                className="p-3 rounded-md bg-success-bg text-success-text text-[13px] border border-success-border flex items-start gap-2"
              >
                <CheckCircle2 size={16} className="shrink-0 mt-0.5" aria-hidden />
                {/* ⚠️ D-422: 「送信しました」と断定しないこと。断定すると、応答の差から
                    アカウントの実在が判別できてしまう */}
                <span>該当するアカウントが登録されている場合、再設定用のリンクをお送りしました。数分待っても届かない場合は下の方法をお試しください。</span>
              </div>
              <FallbackGuidance />
              <button
                type="button"
                onClick={() => { setSubmitted(false); setIdentifier(''); }}
                className="w-full text-[12px] text-chrome-base underline underline-offset-2 min-h-[44px]"
              >
                別のログインIDで試す
              </button>
              <Link
                to="/login"
                className="block text-center text-[12px] text-chrome-base underline underline-offset-2 min-h-[44px] leading-[44px]"
              >
                ログイン画面に戻る
              </Link>
            </div>
          ) : (
            <>
              <p className="text-[13px] text-text-sub mb-4 leading-relaxed">
                ご登録のログインIDまたはメールアドレスを入力してください。
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label htmlFor="identifier" className="block text-[12px] font-bold text-text-sub mb-1.5">
                    ログインIDまたはメールアドレス
                  </label>
                  <input
                    id="identifier"
                    type="text"
                    required
                    autoComplete="username"
                    placeholder="例：yamada2026 または name@example.com"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="w-full h-[44px] px-3 rounded-md bg-surface-card text-text-main text-[13px] placeholder:text-text-muted shadow-[inset_0_0_0_1.5px_#E2DFDA] focus:outline-none focus:shadow-focus-ring transition-shadow"
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  className="w-full"
                  disabled={!identifier.trim()}
                  loading={loading}
                  loadingLabel="送信中…"
                >
                  再設定リンクを送る
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-neutral-line">
                <FallbackGuidance />
              </div>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-[12px] text-chrome-base underline underline-offset-2 inline-flex items-center min-h-[44px] px-2"
                >
                  ログイン画面に戻る
                </Link>
              </div>
            </>
          )}
        </div>
        <p className="text-center text-xs text-text-muted mt-4">© 2026 MiseSpark</p>
      </div>
    </div>
  );
}

/** D-417 のフォールバック階層 (LINE → メール → 管理者) のうち、未ログインの利用者に案内できる2つ。 */
function FallbackGuidance() {
  return (
    <div className="space-y-3 text-[12px] text-text-sub leading-relaxed">
      <div className="flex items-start gap-2">
        <MessageCircle size={16} className="shrink-0 mt-0.5 text-chrome-base" aria-hidden />
        <p>
          <span className="font-bold text-text-main">LINEで受け取る</span><br />
          MiseSparkのLINE公式アカウントと連携済みの場合は、トーク画面で「パスワード」と送信すると再設定リンクを受け取れます。
        </p>
      </div>
      <p>
        <span className="font-bold text-text-main">上記で解決しない場合</span><br />
        店長または管理者にパスワードの再設定を依頼してください。
      </p>
    </div>
  );
}
